import React, { useEffect, useRef, useState, useCallback } from 'react'
import { number, string, func, arrayOf } from 'prop-types'
import { wallColorsType, wallMarkerType } from 'models/propTypes'
import { getRenderer } from 'models/refStore/selectors'

import classNames from 'classnames'

require('./WallPaintPin.scss')

const RIGHT_SIDE_BUFFER = 48 // Pixels - 3rem

const rgbArrayToHex = (color) =>
    `#${color
        .slice(0, 3)
        .map((channel) => {
            const hex = channel.toString(16)
            return hex.length === 1 ? `0${hex}` : hex
        })
        .join('')}`

// https://www.nbdtech.com/Blog/archive/2008/04/27/Calculating-the-Perceived-Brightness-of-a-Color.aspx
const rbgToBrightness = (colorOpt) =>
    colorOpt ? (colorOpt[0] * 299 + colorOpt[1] * 587 + colorOpt[2] * 114) / 1000 : 255

const WallPaintPin = ({ planeIndices, color, setColor, compositionId, wallMarker, colorsList, onPinInteract }) => {
    const containerRef = useRef()
    const pickerRef = useRef()
    const [renderLeft, setRenderLeft] = useState(false)

    const reposition = useCallback(() => {
        // Set renderLeft true if there isn't enough space for the
        // picker on the right of the dot
        const { offsetWidth: pickerWidth } = pickerRef.current
        const { offsetWidth: containerWidth } = containerRef.current
        const spaceRight = containerWidth * ((100 - wallMarker[0]) / 100)
        setRenderLeft(spaceRight <= pickerWidth + RIGHT_SIDE_BUFFER)
    }, [wallMarker])

    useEffect(() => {
        const renderer = getRenderer()
        if (renderer && renderer.compositeBg) {
            planeIndices.forEach((planeIndex) => renderer.compositeBg.setPlaneColor(planeIndex, color, true))
        }
    }, [color])

    useEffect(reposition, [wallMarker])
    useEffect(() => {
        window.addEventListener('resize', reposition)
        return () => {
            window.removeEventListener('resize', reposition)
        }
    }, [wallMarker])

    if (!Array.isArray(colorsList) || colorsList.length === 0 || !Array.isArray(wallMarker) || wallMarker.length < 2) {
        return null
    }

    const setColorWrapped = (colorString) => {
        const timestamp = Date.now() // For analytics to sync the individual events
        planeIndices.forEach((planeIndex) => {
            setColor(compositionId, planeIndex, colorString, { timestamp })
        })
    }

    const dotStyle = {
        left: `${wallMarker[0]}%`,
        top: `${wallMarker[1]}%`,
    }

    const colors = []
    const colorBrightness = {}
    colorsList.forEach((rgb) => {
        const hex = rgbArrayToHex(rgb)
        colors.push(hex)
        colorBrightness[hex] = rbgToBrightness(rgb)
    })

    // Option to unset the color
    if (renderLeft) {
        if (colors.length >= 5) {
            colors.splice(5, 0, null)
        } else {
            colors.push(null)
        }
    } else {
        colors.unshift(null)
    }

    const colorOptions = colors.map((colorOpt) => {
        const selected = colorOpt === color
        const brightness = colorBrightness[colorOpt]
        const checkmarkColor = brightness > 192 ? '#111' : `white`

        return (
            <div
                key={colorOpt}
                className={classNames('colorPick')}
                onClick={(e) => {
                    e.stopPropagation()
                    setColorWrapped(colorOpt)
                }}
            >
                <div
                    className={classNames('colorSwatch', {
                        selected,
                        bright: colorOpt ? brightness > 225 : true,
                    })}
                    style={{ backgroundColor: colorOpt || 'white' }}
                />
                <div className={classNames('colorSelect', { selected, noColor: !colorOpt })} />
                {colorOpt && selected && (
                    <div className="colorCheckmark">
                        <svg width="12" height="10" viewBox="0 0 12 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M10.5591 0.499969L12 1.96078L4.56365 9.49997L0 4.87322L1.44089 3.4124L4.56365 6.57835L10.5591 0.499969Z"
                                fill={checkmarkColor}
                            />
                        </svg>
                    </div>
                )}
                {!colorOpt && <div className="diagonal" />}
            </div>
        )
    })

    return (
        <div className="WallPaintPin" ref={containerRef}>
            <div
                className="dotWrapper"
                style={dotStyle}
                onMouseEnter={() => onPinInteract(true)}
                onMouseLeave={() => onPinInteract(false)}
            >
                <div className="outerDot" />
                <div className={classNames('pickerContainer', { renderLeft })}>
                    <div className="pickerRoot" ref={pickerRef}>
                        {colorOptions}
                    </div>
                </div>
            </div>
        </div>
    )
}

WallPaintPin.defaultProps = {
    color: null,
    wallMarker: null,
    colorsList: null,
}

WallPaintPin.propTypes = {
    planeIndices: arrayOf(number).isRequired,
    color: string,
    setColor: func.isRequired,
    compositionId: number.isRequired,
    wallMarker: wallMarkerType,
    colorsList: wallColorsType,
    onPinInteract: func.isRequired,
}

export default WallPaintPin
